import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock, CheckCircle, X, RefreshCw, Filter, Calendar, ChevronLeft, ChevronRight, Scissors, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const API_BASE = '/cutting-simulator';

// Structural classification options (always available)
const STRUCTURAL_OPTIONS = [
  { value: 'exclude', label: 'Excluir dos Cálculos', color: 'text-red-500', bg: 'bg-red-500/10', desc: 'Não contabilizar' },
  { value: 'reclassify_stopped', label: 'Reclassificar Máq. Parada', color: 'text-pink-500', bg: 'bg-pink-500/10', desc: 'Mover para intra marcadas' },
  { value: 'reclassify_between', label: 'Reclassificar Entre Marc.', color: 'text-cyan-500', bg: 'bg-cyan-500/10', desc: 'Mover para entre marcadas' },
  { value: 'normal', label: 'Normal (Manter)', color: 'text-emerald-500', bg: 'bg-emerald-500/10', desc: 'Manter nos cálculos' },
];

// Default reasons if settings not available
const DEFAULT_REASONS = [
  { id: 'breakdown', name: 'Avaria', color: '#ef4444' },
  { id: 'material', name: 'Falta de Material', color: '#f59e0b' },
  { id: 'setup', name: 'Setup/Preparação', color: '#3b82f6' },
  { id: 'maintenance', name: 'Manutenção Preventiva', color: '#8b5cf6' },
  { id: 'operator', name: 'Ausência Operador', color: '#ec4899' },
  { id: 'other', name: 'Outro', color: '#6b7280' },
];

// Format decimal minutes to MM:SS
const formatMinutesToMMSS = (decimalMinutes) => {
  if (!decimalMinutes || isNaN(decimalMinutes)) return "0:00";
  const totalSeconds = Math.round(decimalMinutes * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Classification label translator
const getClassificationLabel = (classification, anomalousReasons) => {
  const translations = {
    'shift_transition': 'Transição de Turno',
    'exclude': 'Excluído dos Cálculos',
    'normal': 'Normal',
    'reclassify_stopped': 'Reclassif. Máq. Parada',
    'reclassify_between': 'Reclassif. Entre Marc.',
  };
  return anomalousReasons.find(r => r.id === classification)?.name 
    || STRUCTURAL_OPTIONS.find(o => o.value === classification)?.label 
    || translations[classification] 
    || classification;
};

function AnomalousRecordsPanel({ t, settings, onClose, onRecordsClassified }) {
  const anomalousReasons = settings?.anomalousReasons || DEFAULT_REASONS;
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [classifications, setClassifications] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualAnomaly, setManualAnomaly] = useState({ date: '', field_type: 'machine_stopped', minutes: '', reason: '' });
  const [manualDayData, setManualDayData] = useState(null);
  const [loadingDayData, setLoadingDayData] = useState(false);
  
  // Date filter state
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all', 'week', 'month', 'custom'
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });

  // Fetch anomalous summary
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API_BASE}/anomalous-summary`);
      const data = res.data;
      setSummary(data);
      
      // Auto-select first day with unclassified records
      if (data.days_with_anomalous?.length > 0) {
        const firstUnclassified = data.days_with_anomalous.find(d => d.unclassified_count > 0);
        if (firstUnclassified) {
          setSelectedDay(firstUnclassified);
        } else {
          setSelectedDay(data.days_with_anomalous[0]);
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar registos anormais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Filter days by period
  const getFilteredDays = () => {
    if (!summary?.days_with_anomalous) return [];
    const days = summary.days_with_anomalous;
    
    if (periodFilter === 'all') return days;
    
    const today = new Date();
    let startDate, endDate;
    
    if (periodFilter === 'week') {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay() + 1);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    } else if (periodFilter === 'month') {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (periodFilter === 'custom' && customDateRange.start && customDateRange.end) {
      startDate = new Date(customDateRange.start);
      endDate = new Date(customDateRange.end);
    } else {
      return days;
    }
    
    return days.filter(d => {
      const date = new Date(d.date);
      return date >= startDate && date <= endDate;
    });
  };

  const filteredDays = getFilteredDays();

  // Handle classification change - supports separate reason and action
  const handleClassificationChange = (record, value, type = 'auto') => {
    const key = `${record.date}_${record.record_index}_${record.field_type}`;
    const existing = classifications[key] || {};
    
    // Determine if it's a reason or action
    const isAction = STRUCTURAL_OPTIONS.some(o => o.value === value);
    const isReason = anomalousReasons.some(r => r.id === value);
    
    const updated = { ...existing,
      record_index: record.record_index,
      date: record.date,
      field_type: record.field_type,
      original_value: record.original_value,
    };
    
    if (isReason) {
      updated.reason = anomalousReasons.find(r => r.id === value)?.name || value;
      updated.reason_id = value;
      // Set default action if not set yet
      if (!updated.classification) updated.classification = 'exclude';
    } else if (isAction) {
      updated.classification = value;
      updated.reason = updated.reason || existing.reason || 
        anomalousReasons.find(r => r.id === updated.reason_id)?.name || 
        STRUCTURAL_OPTIONS.find(o => o.value === value)?.label || value;
    } else {
      updated.classification = value;
      updated.reason = value;
    }
    
    setClassifications(prev => ({ ...prev, [key]: updated }));
  };

  // Get current reason and action for a record  
  const getRecordState = (record) => {
    const key = `${record.date}_${record.record_index}_${record.field_type}`;
    const local = classifications[key];
    // Try to find reason_id from saved classification_reason name
    const savedReasonName = record.classification_reason || record.anomalous_reason || '';
    const matchedReason = anomalousReasons.find(r => r.name === savedReasonName || r.id === savedReasonName);
    return {
      reason_id: local?.reason_id || matchedReason?.id || null,
      action: local?.classification || record.classification || null,
      reason: local?.reason || savedReasonName || null,
    };
  };

  // Save classifications
  const saveClassifications = async () => {
    const toSave = Object.values(classifications);
    if (toSave.length === 0) {
      toast.warning('Nenhuma classificação para guardar');
      return;
    }

    setSaving(true);
    try {
      await api.post(`${API_BASE}/anomalous/classify`, { classifications: toSave });
      
      toast.success(`${toSave.length} classificação(ões) guardada(s)`);
      setClassifications({});
      fetchSummary();
      if (onRecordsClassified) onRecordsClassified();
    } catch (error) {
      toast.error('Erro ao guardar classificações');
    } finally {
      setSaving(false);
    }
  };

  // Reset classifications for a day
  const resetDay = async (date) => {
    if (!confirm(`Tem a certeza que deseja repor todas as classificações de ${date}?`)) return;

    try {
      await api.post(`${API_BASE}/anomalous/reset/${date}`);
      
      toast.success('Classificações repostas');
      fetchSummary();
      if (onRecordsClassified) onRecordsClassified();
    } catch (error) {
      toast.error('Erro ao repor classificações');
    }
  };

  // Get current classification for a record
  const getClassification = (record) => {
    const key = `${record.date}_${record.record_index}_${record.field_type}`;
    return classifications[key]?.classification || record.classification;
  };

  // Register manual anomaly for a specific day
  const fetchDayDataForManual = async (date) => {
    if (!date) { setManualDayData(null); return; }
    setLoadingDayData(true);
    try {
      const res = await api.get(`${API_BASE}/days/${date}`);
      const d = res.data;
      const metrics = d.metrics || {};
      const fmtMin = (m) => {
        if (!m || m <= 0) return '0:00';
        const h = Math.floor(m / 60);
        const min = Math.floor(m % 60);
        return h > 0 ? `${h}:${String(min).padStart(2,'0')}` : `${min}min`;
      };
      setManualDayData({
        total_cuts: metrics.total_cuts || 0,
        total_machine_stopped: metrics.total_machine_stopped || 0,
        total_between_markers: metrics.total_between_markers || 0,
        total_cutting_time: metrics.total_cutting_time || 0,
        first_start: metrics.first_start || d.first_start || '-',
        last_end: metrics.last_end || d.last_end || '-',
        first_start_minutes: metrics.first_start_minutes || 0,
        last_end_minutes: metrics.last_end_minutes || 0,
        overtime_gap_outside_hours: metrics.overtime_gap_outside_hours || 0,
        fmtStopped: fmtMin(metrics.total_machine_stopped || 0),
        fmtBetween: fmtMin(metrics.total_between_markers || 0),
        fmtCutting: fmtMin(metrics.total_cutting_time || 0),
      });
    } catch {
      setManualDayData(null);
    } finally {
      setLoadingDayData(false);
    }
  };

  const registerManualAnomaly = async () => {
    if (!manualAnomaly.date || !manualAnomaly.minutes || !manualAnomaly.reason) {
      toast.warning('Preencha todos os campos');
      return;
    }
    try {
      // Use subtract-time endpoint to actually reduce the time
      await api.post(`/cutting-simulator/days/${manualAnomaly.date}/subtract-time`, {
        field_type: manualAnomaly.field_type,
        minutes: parseFloat(manualAnomaly.minutes),
        reason: anomalousReasons.find(r => r.id === manualAnomaly.reason)?.name || manualAnomaly.reason,
      });
      toast.success(`-${manualAnomaly.minutes}min ${manualAnomaly.field_type === 'machine_stopped' ? 'Intra Marc.' : 'Entre Marc.'} subtraído`);
      setShowAddManual(false);
      setManualAnomaly({ date: '', field_type: 'machine_stopped', minutes: '', reason: '' });
      setManualDayData(null);
      fetchSummary();
      if (onRecordsClassified) onRecordsClassified();
    } catch (error) {
      toast.error('Erro ao registar anomalia');
    }
  };

  // Delete manual adjustment record
  const deleteManualRecord = async (date, recordIndex) => {
    if (!confirm('Tem a certeza que deseja apagar este registo manual?')) return;
    try {
      await api.delete(`/cutting-simulator/days/${date}/adjustment/${recordIndex}`);
      toast.success('Registo manual apagado');
      fetchSummary();
      if (onRecordsClassified) onRecordsClassified();
    } catch (error) {
      toast.error('Erro ao apagar registo');
    }
  };

  if (loading) {
    return (
      <div className={`${t.card} rounded-xl p-8 text-center`}>
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-amber-500" />
        <p className={t.textMut}>A verificar registos anormais...</p>
      </div>
    );
  }

  if (!summary || summary.total_anomalous === 0) {
    return (
      <div className={`${t.card} rounded-xl p-8 text-center`}>
        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500/50" />
        <h3 className={`text-lg font-semibold ${t.text} mb-2`}>Sem Registos Anormais</h3>
        <p className={t.textMut}>Todos os registos estão dentro dos parâmetros normais.</p>
        {onClose && (
          <Button onClick={onClose} variant="outline" className="mt-4">
            Fechar
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`${t.card} rounded-xl p-6`} data-testid="anomalous-records-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-amber-500/20">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${t.text}`}>Registos Anormais</h2>
            <p className={`text-sm ${t.textMut}`}>
              {summary.total_anomalous} registo{summary.total_anomalous !== 1 ? 's' : ''} • 
              {summary.unclassified_count} por classificar
              {filteredDays.length < (summary.days_with_anomalous?.length || 0) && (
                <span className="text-amber-500"> • {filteredDays.length} dia(s) filtrado(s)</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setShowAddManual(true)}
            variant="outline"
            className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
          >
            <Plus className="w-4 h-4 mr-1" />
            Registar Anomalia
          </Button>
          {Object.keys(classifications).length > 0 && (
            <Button 
              onClick={saveClassifications} 
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Guardar ({Object.keys(classifications).length})
            </Button>
          )}
          {onClose && (
            <Button onClick={onClose} variant="outline" size="icon">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Manual Anomaly Registration Modal */}
      {showAddManual && (
        <div className={`${t.inner} rounded-lg p-5 mb-4 border-2 border-amber-500/30`}>
          <h3 className={`text-sm font-semibold ${t.text} mb-4`}>Registar Anomalia Manual</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={`text-xs ${t.textMut} block mb-1`}>Data</label>
              <input
                type="date"
                value={manualAnomaly.date}
                onChange={(e) => {
                  setManualAnomaly(prev => ({ ...prev, date: e.target.value }));
                  fetchDayDataForManual(e.target.value);
                }}
                className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
              />
            </div>
            <div>
              <label className={`text-xs ${t.textMut} block mb-1`}>Tipo</label>
              <select
                value={manualAnomaly.field_type}
                onChange={(e) => setManualAnomaly(prev => ({ ...prev, field_type: e.target.value }))}
                className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
              >
                <option value="machine_stopped">Intra Marcadas (Máq. Parada)</option>
                <option value="between_markers">Entre Marcadas</option>
              </select>
            </div>
            <div>
              <label className={`text-xs ${t.textMut} block mb-1`}>Minutos</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={manualAnomaly.minutes}
                onChange={(e) => setManualAnomaly(prev => ({ ...prev, minutes: e.target.value }))}
                placeholder="Ex: 45"
                className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
              />
            </div>
            <div>
              <label className={`text-xs ${t.textMut} block mb-1`}>Motivo</label>
              <select
                value={manualAnomaly.reason}
                onChange={(e) => setManualAnomaly(prev => ({ ...prev, reason: e.target.value }))}
                className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
              >
                <option value="">Selecionar...</option>
                {anomalousReasons.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Day data summary - shows when date is selected */}
          {loadingDayData && (
            <div className={`mt-3 p-3 rounded-lg ${t.card} text-center`}>
              <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
              <span className={`text-xs ${t.textMut}`}>A carregar dados do dia...</span>
            </div>
          )}
          {manualDayData && !loadingDayData && (() => {
            const mins = parseFloat(manualAnomaly.minutes) || 0;
            const isIntra = manualAnomaly.field_type === 'machine_stopped';
            const totalTime = manualDayData.total_cutting_time + manualDayData.total_machine_stopped + manualDayData.total_between_markers;
            // Current percentages
            const curPctCorte = totalTime > 0 ? (manualDayData.total_cutting_time / totalTime * 100) : 0;
            const curPctIntra = totalTime > 0 ? (manualDayData.total_machine_stopped / totalTime * 100) : 0;
            const curPctEntre = totalTime > 0 ? (manualDayData.total_between_markers / totalTime * 100) : 0;
            // After adjustment
            const newStopped = Math.max(0, manualDayData.total_machine_stopped - (isIntra ? mins : 0));
            const newBetween = Math.max(0, manualDayData.total_between_markers - (!isIntra ? mins : 0));
            const newTotal = manualDayData.total_cutting_time + newStopped + newBetween;
            const pctCorte = newTotal > 0 ? (manualDayData.total_cutting_time / newTotal * 100) : 0;
            const pctIntra = newTotal > 0 ? (newStopped / newTotal * 100) : 0;
            const pctEntre = newTotal > 0 ? (newBetween / newTotal * 100) : 0;
            const fmtHM = (m) => { const h=Math.floor(Math.abs(m)/60); const min=Math.floor(Math.abs(m)%60); return `${h}h${String(min).padStart(2,'0')}`; };
            // T.Total do dia (elapsed - breaks)
            const elapsed = (manualDayData.last_end_minutes || 0) - (manualDayData.first_start_minutes || 0);
            const overtimeGap = manualDayData.overtime_gap_outside_hours || 0;
            const tTotalDia = Math.max(0, elapsed - 110 - overtimeGap);
            
            return (
            <div className={`mt-3 p-4 rounded-lg ${t.card} border border-sky-500/30`} data-testid="manual-day-summary">
              <div className={`text-xs font-semibold ${t.text} mb-3`}>Dados do Dia {manualAnomaly.date}</div>
              
              {/* Current values with percentages */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                <div className="text-center">
                  <div className={`text-[10px] ${t.textMut}`}>Horário</div>
                  <div className={`text-xs font-mono ${t.text}`}>{manualDayData.first_start?.substring(0,5)} - {manualDayData.last_end?.substring(0,5)}</div>
                </div>
                <div className="text-center">
                  <div className={`text-[10px] ${t.textMut}`}>Cortes</div>
                  <div className={`text-sm font-bold ${t.text}`}>{manualDayData.total_cuts}</div>
                  <div className={`text-[9px] ${t.textMut}`}>T.Dia: {tTotalDia > 0 ? fmtHM(tTotalDia) : fmtHM(totalTime)}</div>
                </div>
                <div className="text-center">
                  <div className={`text-[10px] ${t.textMut}`}>T.Corte</div>
                  <div className="text-sm font-bold text-emerald-500">{fmtHM(manualDayData.total_cutting_time)}</div>
                  <div className={`text-[9px] ${t.textMut}`}>{manualDayData.total_cutting_time.toFixed(0)} min</div>
                  <div className="text-[9px] text-emerald-500 font-medium">{curPctCorte.toFixed(1)}%</div>
                </div>
                <div className={`text-center p-1.5 rounded ${isIntra ? 'bg-amber-500/10 ring-1 ring-amber-500/40' : ''}`}>
                  <div className={`text-[10px] ${t.textMut}`}>Intra Marc.</div>
                  <div className="text-sm font-bold text-amber-500">{fmtHM(manualDayData.total_machine_stopped)}</div>
                  <div className={`text-[9px] ${t.textMut}`}>{manualDayData.total_machine_stopped.toFixed(0)} min</div>
                  <div className="text-[9px] text-amber-500 font-medium">{curPctIntra.toFixed(1)}%</div>
                </div>
                <div className={`text-center p-1.5 rounded ${!isIntra ? 'bg-cyan-500/10 ring-1 ring-cyan-500/40' : ''}`}>
                  <div className={`text-[10px] ${t.textMut}`}>Entre Marc.</div>
                  <div className="text-sm font-bold text-cyan-500">{fmtHM(manualDayData.total_between_markers)}</div>
                  <div className={`text-[9px] ${t.textMut}`}>{manualDayData.total_between_markers.toFixed(0)} min</div>
                  <div className="text-[9px] text-cyan-500 font-medium">{curPctEntre.toFixed(1)}%</div>
                </div>
                <div className="text-center">
                  <div className={`text-[10px] ${t.textMut}`}>Soma 3T</div>
                  <div className={`text-sm font-bold ${t.text}`}>{fmtHM(totalTime)}</div>
                  <div className={`text-[9px] ${t.textMut}`}>{totalTime.toFixed(0)} min</div>
                </div>
              </div>
              
              {/* Preview after adjustment */}
              {mins > 0 && (
                <div className="mt-2 pt-2 border-t border-dashed border-sky-500/30">
                  <div className={`text-[10px] font-semibold ${t.textMut} mb-2`}>Após ajuste de -{mins} min {isIntra ? 'Intra Marc.' : 'Entre Marc.'}:</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-2 rounded bg-emerald-500/10">
                      <div className="text-[10px] text-emerald-600">% Corte</div>
                      <div className="text-lg font-bold text-emerald-600">{pctCorte.toFixed(1)}%</div>
                      <div className="text-[9px] text-emerald-600/70">(era {curPctCorte.toFixed(1)}%)</div>
                    </div>
                    <div className="text-center p-2 rounded bg-amber-500/10">
                      <div className="text-[10px] text-amber-600">% Intra</div>
                      <div className="text-lg font-bold text-amber-600">{pctIntra.toFixed(1)}%</div>
                      <div className="text-[9px] text-amber-600/70">{isIntra ? `${fmtHM(newStopped)} (${newStopped.toFixed(0)}min)` : `(era ${curPctIntra.toFixed(1)}%)`}</div>
                    </div>
                    <div className="text-center p-2 rounded bg-cyan-500/10">
                      <div className="text-[10px] text-cyan-600">% Entre</div>
                      <div className="text-lg font-bold text-cyan-600">{pctEntre.toFixed(1)}%</div>
                      <div className="text-[9px] text-cyan-600/70">{!isIntra ? `${fmtHM(newBetween)} (${newBetween.toFixed(0)}min)` : `(era ${curPctEntre.toFixed(1)}%)`}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
          {manualAnomaly.date && !manualDayData && !loadingDayData && (
            <div className={`mt-3 p-3 rounded-lg ${t.card} border border-red-500/30 text-center`}>
              <span className="text-xs text-red-500">Sem dados para esta data</span>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowAddManual(false); setManualDayData(null); }}>Cancelar</Button>
            <Button 
              onClick={registerManualAnomaly}
              disabled={!manualAnomaly.date || !manualAnomaly.minutes || !manualAnomaly.reason}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Registar
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className={`${t.inner} rounded-lg p-4 mb-4 border-l-4 border-amber-500`}>
        <p className={`text-sm ${t.text}`}>
          <strong>Regra de Detecção:</strong> Tempos superiores a 20 minutos (máquina parada ou entre marcadas) 
          são considerados anormais e devem ser classificados.
        </p>
        <p className={`text-xs ${t.textMut} mt-2`}>
          Pode reclassificar tempos (Máq. Parada ↔ Entre Marcadas), excluir como pausa, ou marcar como normal.
        </p>
      </div>

      {/* Period Filter */}
      <div className={`flex items-center gap-2 mb-4 flex-wrap`}>
        <Calendar className={`w-4 h-4 ${t.textMut}`} />
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'week', label: 'Semana' },
            { value: 'month', label: 'Mês' },
            { value: 'custom', label: 'Data' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => { setPeriodFilter(opt.value); setSelectedDay(null); }}
              className={`px-3 py-1.5 text-xs font-medium ${
                periodFilter === opt.value ? 'bg-amber-500 text-white' : `${t.inner} ${t.textSec}`
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {periodFilter === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customDateRange.start}
              onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
              className={`px-2 py-1 text-xs rounded ${t.inputBg} ${t.card} ${t.text}`}
            />
            <span className={t.textMut}>-</span>
            <input
              type="date"
              value={customDateRange.end}
              onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
              className={`px-2 py-1 text-xs rounded ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
        )}
      </div>

      {/* Days with Anomalous Records */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Days List */}
        <div className={`${t.inner} rounded-lg p-4`}>
          <h3 className={`text-sm font-semibold ${t.text} mb-3`}>
            Dias com Anomalias
            <span className={`text-xs font-normal ${t.textMut} ml-2`}>({filteredDays.length})</span>
          </h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredDays.length === 0 ? (
              <p className={`text-sm ${t.textMut} text-center py-4`}>Nenhum dia encontrado no período</p>
            ) : filteredDays.map(day => {
              // Aggregate info for the card
              const totalMin = day.anomalous_records.reduce((s, r) => s + (r.original_value || 0), 0);
              const reasons = [...new Set(day.anomalous_records
                .map(r => r.classification_reason || r.classification || r.reason_hint?.split(' ')[0])
                .filter(Boolean)
              )].slice(0, 2);
              const types = day.anomalous_records.map(r => r.field_type);
              const hasIntra = types.includes('machine_stopped');
              const hasEntre = types.includes('between_markers');
              const hasManual = day.anomalous_records.some(r => r.is_time_adjustment);
              const fmtMin = (m) => { const h=Math.floor(m/60); const min=Math.floor(m%60); return h > 0 ? `${h}h${String(min).padStart(2,'0')}` : `${min}min`; };
              // Most recent classified_at or created_at
              const dates = day.anomalous_records
                .map(r => r.classified_at || r.created_at)
                .filter(Boolean)
                .sort().reverse();
              const lastAction = dates[0] 
                ? new Date(dates[0]).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) 
                : (day.anomalous_records.some(r => r.classification) ? `Classificado (s/ data)` : null);
              
              return (
              <button
                key={day.date}
                onClick={() => setSelectedDay(day)}
                className={`w-full p-3 rounded-lg text-left transition-colors ${
                  selectedDay?.date === day.date 
                    ? 'bg-amber-500/20 border border-amber-500' 
                    : `${t.card} hover:opacity-80`
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${t.text}`}>{day.date}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    day.unclassified_count > 0 
                      ? 'bg-red-500/10 text-red-500' 
                      : 'bg-emerald-500/10 text-emerald-500'
                  }`}>
                    {day.unclassified_count > 0 ? `${day.unclassified_count} por classificar` : 'Classificado'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`text-sm font-bold ${t.text}`}>{fmtMin(totalMin)}</span>
                  <span className={`text-[10px] ${t.textMut}`}>({day.total_anomalous} reg.)</span>
                  {hasIntra && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-500">Intra</span>}
                  {hasEntre && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">Entre</span>}
                  {hasManual && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-500">Manual</span>}
                </div>
                {reasons.length > 0 && (
                  <div className={`text-[10px] ${t.textMut} mt-1 truncate`}>
                    {reasons.map(r => getClassificationLabel(r, anomalousReasons)).join(', ')}
                  </div>
                )}
                {lastAction && (
                  <div className={`text-[9px] ${t.textMut} mt-0.5`}>
                    Criado: {lastAction}
                  </div>
                )}
              </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Records */}
        <div className="lg:col-span-2">
          {selectedDay ? (
            <div className={`${t.inner} rounded-lg p-4`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm font-semibold ${t.text}`}>
                  Registos de {selectedDay.date}
                </h3>
                <button
                  onClick={() => resetDay(selectedDay.date)}
                  className={`text-xs px-2 py-1 rounded ${t.card} ${t.textMut} hover:text-red-500`}
                >
                  Repor Classificações
                </button>
              </div>
              
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {selectedDay.anomalous_records.map((record, idx) => {
                  const existingClassification = record.classification;
                  const state = getRecordState(record);
                  const isAlreadyClassified = !!existingClassification;
                  const isManual = record.start_time === 'Ajuste' || record.plan_name?.includes('[Ajuste Manual]');
                  
                  return (
                    <div 
                      key={`${record.record_index}_${record.field_type}_${idx}`}
                      className={`p-4 rounded-lg border ${
                        isManual ? 'bg-violet-500/5 border-violet-500/40' :
                        isAlreadyClassified ? `${t.card} border-emerald-500/30` : 
                        `${t.card} border-amber-500/50`
                      }`}
                    >
                      {/* Record Info */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            record.field_type === 'machine_stopped' 
                              ? 'bg-red-500/10 text-red-500' 
                              : 'bg-amber-500/10 text-amber-500'
                          }`}>
                            {record.field_type === 'machine_stopped' ? 'Máq. Parada' : 'Entre Marcadas'}
                          </span>
                          {isAlreadyClassified && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-500">
                              Classificado: {getClassificationLabel(existingClassification, anomalousReasons)}
                            </span>
                          )}
                          {isManual && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-500/10 text-violet-500">
                              Ajuste Manual
                            </span>
                          )}
                          <span className={`text-xl font-bold ${isManual ? 'text-violet-600' : t.text}`}>
                            {isManual ? `-${formatMinutesToMMSS(Math.abs(record.original_value))}` : formatMinutesToMMSS(record.original_value)}
                          </span>
                          <span className={`text-xs ${t.textMut}`}>
                            ({Math.abs(record.original_value).toFixed(1)} min)
                          </span>
                          {/* Delete button for manual records */}
                          {isManual && (
                            <button 
                              onClick={() => deleteManualRecord(record.date, record.record_index)}
                              className="ml-auto px-2 py-1 text-xs rounded bg-red-500/10 text-red-500 hover:bg-red-500/20"
                              title="Apagar registo manual"
                            >
                              <Trash2 className="w-3.5 h-3.5 inline mr-1" />Apagar
                            </button>
                          )}
                        </div>
                        <div className={`text-xs ${t.textMut} space-y-1`}>
                          <p><Clock className="w-3 h-3 inline mr-1" />{record.start_time} → {record.end_time}</p>
                          {isManual && record.plan_name && (
                            <p className="text-violet-600 font-medium">{record.plan_name.replace('[Ajuste Manual] ', '')}</p>
                          )}
                          {record.reason_hint && <p>{record.reason_hint}</p>}
                          {!isManual && record.plan_name && (
                            <p><Scissors className="w-3 h-3 inline mr-1" />{record.plan_name}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Classification: Motivo (Reason) */}
                      {!isManual && (
                      <>
                      <div className="mb-2">
                        <div className={`text-[10px] uppercase tracking-wider ${t.textMut} mb-1.5`}>Motivo</div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {anomalousReasons.map(reason => (
                            <button
                              key={reason.id}
                              onClick={() => handleClassificationChange(record, reason.id, 'reason')}
                              className={`px-3 py-2 text-xs rounded-lg transition-colors text-left flex items-center gap-2 ${
                                state.reason_id === reason.id
                                  ? 'font-medium ring-2 ring-offset-1'
                                  : `${t.card} ${t.textMut} hover:opacity-80`
                              }`}
                              style={state.reason_id === reason.id ? { 
                                backgroundColor: reason.color + '20', 
                                color: reason.color,
                                ringColor: reason.color 
                              } : {}}
                            >
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: reason.color }} />
                              {reason.name}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Classification: Acção (Action) */}
                      <div>
                        <div className={`text-[10px] uppercase tracking-wider ${t.textMut} mb-1.5`}>Acção</div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {STRUCTURAL_OPTIONS.map(option => (
                            <button
                              key={option.value}
                              onClick={() => handleClassificationChange(record, option.value, 'action')}
                              className={`px-3 py-2 text-xs rounded-lg transition-colors text-left ${
                                state.action === option.value
                                  ? `${option.bg} ${option.color} font-medium ring-2 ring-offset-1 ring-current`
                                  : `${t.card} ${t.textMut} hover:opacity-80`
                              }`}
                              title={option.desc}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      </>
                      )}
                      
                      {(state.reason_id || state.action) && (
                        <div className={`mt-3 pt-3 border-t ${t.divider} flex items-center gap-2`}>
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className={`text-xs font-medium ${t.text}`}>
                            {state.reason_id ? (anomalousReasons.find(r => r.id === state.reason_id)?.name || state.reason_id) : ''}
                            {state.reason_id && state.action ? ' → ' : ''}
                            {state.action ? (STRUCTURAL_OPTIONS.find(o => o.value === state.action)?.label || getClassificationLabel(state.action, anomalousReasons)) : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={`${t.inner} rounded-lg p-8 text-center`}>
              <Filter className="w-8 h-8 mx-auto mb-3 text-amber-500/50" />
              <p className={t.textMut}>Selecione um dia para ver os registos anormais</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnomalousRecordsPanel;
